// Service Steady PWA — src/lib/parseReminderTime.ts — 2026-07-02 (Batch P4)
//
// Best-effort extraction of a follow-up time from recent conversation
// messages, used to PRE-FILL the "Remind me" sheet (e.g. customer says
// "I'm off after 2:30pm" → sheet opens at 2:30 PM). Resolution is to the
// NEXT occurrence in the device's local time (the owner's phone runs in
// the tenant timezone). Never blocks: callers fall back to now+1h when
// nothing parses. Owner always confirms/adjusts in the sheet, so a wrong
// guess costs one tap — bias toward returning something plausible.

export interface ParsableMessage {
  body: string;
  direction: 'in' | 'out';
}

/** Clamp into the future: if the resolved time already passed, add a day. */
function nextOccurrence(base: Date, h: number, m: number): Date {
  const d = new Date(base);
  d.setHours(h, m, 0, 0);
  if (d.getTime() <= base.getTime()) d.setDate(d.getDate() + 1);
  return d;
}

/**
 * Parse a single text for a time phrase. Returns null when nothing matches.
 * Exported for unit tests.
 */
export function parseTimePhrase(text: string, now: Date): Date | null {
  const t = text.toLowerCase();

  // "tomorrow at 10" / "tomorrow at 10:30am" / "tomorrow morning/afternoon"
  const tomorrow = /tomorrow(?:\s+(morning|afternoon|evening|night))?(?:\s+at\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm)?)?/.exec(t);
  if (tomorrow) {
    const d = new Date(now);
    d.setDate(d.getDate() + 1);
    if (tomorrow[2]) {
      let h = parseInt(tomorrow[2], 10) % 12;
      const m = tomorrow[3] ? parseInt(tomorrow[3], 10) : 0;
      if (tomorrow[4] === 'pm') h += 12;
      else if (!tomorrow[4] && h < 8) h += 12; // bare "tomorrow at 3" → 3 PM
      d.setHours(h, m, 0, 0);
    } else {
      const part = tomorrow[1];
      d.setHours(part === 'afternoon' ? 15 : part === 'evening' || part === 'night' ? 19 : 10, 0, 0, 0);
    }
    return d;
  }

  // Explicit clock time: "2:30pm", "2:30 pm", "after 2:30", "at 3pm", "at 3".
  const clock = /(?:after|at|around|by)?\s*\b(\d{1,2}):(\d{2})\s*(am|pm)?\b/.exec(t);
  if (clock) {
    let h = parseInt(clock[1]!, 10);
    const m = parseInt(clock[2]!, 10);
    if (h >= 0 && h <= 23 && m <= 59) {
      if (clock[3] === 'pm' && h < 12) h += 12;
      else if (clock[3] === 'am' && h === 12) h = 0;
      // No am/pm: assume business-plausible — 1..7 reads as PM ("after 2:30").
      else if (!clock[3] && h >= 1 && h <= 7) h += 12;
      return nextOccurrence(now, h, m);
    }
  }
  // Bare hour with am/pm or after "at": "at 3", "3pm".
  const bare = /(?:\bafter\b|\bat\b|\baround\b|\bby\b)\s*(\d{1,2})\s*(am|pm)?\b|\b(\d{1,2})\s*(am|pm)\b/.exec(t);
  if (bare) {
    const hRaw = bare[1] ?? bare[3];
    const suffix = bare[2] ?? bare[4];
    if (hRaw) {
      let h = parseInt(hRaw, 10);
      if (h >= 1 && h <= 12) {
        if (suffix === 'pm') h = (h % 12) + 12;
        else if (suffix === 'am') h = h % 12;
        else if (h >= 1 && h <= 7) h += 12; // "at 3" → 3 PM
        return nextOccurrence(now, h, 0);
      }
    }
  }

  // Day-part phrases.
  if (/this afternoon/.test(t)) return nextOccurrence(now, 15, 0);
  if (/this morning/.test(t)) return nextOccurrence(now, 10, 0);
  if (/tonight|this evening/.test(t)) return nextOccurrence(now, 19, 0);
  if (/later today/.test(t)) {
    const d = new Date(now.getTime() + 3 * 60 * 60 * 1000);
    return d;
  }
  return null;
}

/**
 * Scan recent messages (newest first) for a time phrase — customer texts
 * first (that's where "I'm off after 2:30" lives), then any message.
 * Null when nothing parses.
 */
export function parseReminderTimeFromMessages(
  messages: ReadonlyArray<ParsableMessage>,
  now: Date = new Date(),
): Date | null {
  const recent = messages.slice(-12).reverse();
  for (const m of recent) {
    if (m.direction !== 'in') continue;
    const hit = parseTimePhrase(m.body, now);
    if (hit) return hit;
  }
  for (const m of recent) {
    const hit = parseTimePhrase(m.body, now);
    if (hit) return hit;
  }
  return null;
}
